import { ErrorCode } from './types'

/**
 * Plafond de temps cumulé raisonnable (1 an). Au-delà, on considère que le
 * module/driver SCORM a envoyé une durée aberrante (ex. horloge système fausse)
 * et on ignore la valeur plutôt que de stocker un temps impossible.
 */
const MAX_TOTAL_TIME_MS = 366 * 24 * 60 * 60 * 1000

/** Garde-fou de plage pour les scores absolus (raw/min/max). */
const MAX_SCORE_ABS = 1_000_000

/**
 * Parse et valide un score SCORM. Renvoie null si la valeur est absente, non
 * finie (NaN/Infinity) ou hors d'une plage plausible — pour éviter de propager
 * des valeurs poubelle (ex. 3.99e+49) émises par un module/driver buggé.
 */
function sanitizeScore(value: string | undefined, max = MAX_SCORE_ABS): number | null {
  if (!value) return null
  const n = Number.parseFloat(value)
  if (!Number.isFinite(n) || Math.abs(n) > max) return null
  return n
}

/** Parse an ISO 8601 duration (e.g. PT1H30M45.5S) to milliseconds */
function iso8601DurationToMs(duration: string): number {
  const match = duration.match(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?$/)
  if (!match) return 0
  const hours = Number.parseInt(match[4] || '0', 10)
  const minutes = Number.parseInt(match[5] || '0', 10)
  const seconds = Number.parseFloat(match[6] || '0')
  const ms = ((hours * 3600) + (minutes * 60) + seconds) * 1000
  // Borne les durées aberrantes (horloge système fausse côté contenu SCORM).
  if (!Number.isFinite(ms) || ms < 0 || ms > MAX_TOTAL_TIME_MS) return 0
  return ms
}

/** Convert milliseconds to ISO 8601 duration (PT#H#M#.##S) */
function msToIso8601Duration(ms: number): string {
  const totalSeconds = Math.round(ms / 10) / 100 // round to centiseconds
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Number.parseFloat((totalSeconds % 60).toFixed(2))
  return `PT${hours}H${minutes}M${seconds}S`
}

type ScormData = {
  completionStatus?: string
  successStatus?: string
  scoreRaw?: number | null
  scoreMin?: number | null
  scoreMax?: number | null
  scoreScaled?: number | null
  totalTime?: string | null
  sessionTime?: string | null
  suspendData?: string | null
  location?: string | null
}

type OnCommitCallback = (data: ScormData) => Promise<void>

export class ScormApiBridge {
  private data: Record<string, string> = {}
  private initialized = false
  private terminated = false
  private lastError: ErrorCode = ErrorCode.NO_ERROR
  private onCommit: OnCommitCallback
  private initialTotalTimeMs: number

  constructor(initialData: ScormData | null, onCommit: OnCommitCallback) {
    this.onCommit = onCommit
    this.initialTotalTimeMs = 0

    if (initialData) {
      if (initialData.completionStatus) this.data['cmi.completion_status'] = initialData.completionStatus.toLowerCase().replace('_', ' ')
      if (initialData.successStatus) this.data['cmi.success_status'] = initialData.successStatus.toLowerCase()
      if (initialData.scoreRaw != null) this.data['cmi.score.raw'] = String(initialData.scoreRaw)
      if (initialData.scoreMin != null) this.data['cmi.score.min'] = String(initialData.scoreMin)
      if (initialData.scoreMax != null) this.data['cmi.score.max'] = String(initialData.scoreMax)
      if (initialData.scoreScaled != null) this.data['cmi.score.scaled'] = String(initialData.scoreScaled)
      if (initialData.totalTime) {
        this.data['cmi.total_time'] = initialData.totalTime
        this.initialTotalTimeMs = iso8601DurationToMs(initialData.totalTime)
      }
      if (initialData.suspendData) this.data['cmi.suspend_data'] = initialData.suspendData
      if (initialData.location) this.data['cmi.location'] = initialData.location
      this.data['cmi.entry'] = initialData.suspendData || initialData.location ? 'resume' : 'ab-initio'
    } else {
      this.data['cmi.entry'] = 'ab-initio'
    }

    this.data['cmi.completion_status'] = this.data['cmi.completion_status'] || 'not attempted'
    this.data['cmi.success_status'] = this.data['cmi.success_status'] || 'unknown'
    this.data['cmi.exit'] = ''
    this.data['cmi.mode'] = 'normal'
    this.data['cmi.credit'] = 'credit'
  }

  Initialize(_param: string = ''): string {
    if (this.initialized) {
      this.lastError = ErrorCode.ALREADY_INITIALIZED
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.CONTENT_INSTANCE_TERMINATED
      return 'false'
    }
    this.initialized = true
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  Terminate(_param: string = ''): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.TERMINATE_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.TERMINATE_AFTER_TERMINATE
      return 'false'
    }

    this.Commit()
    this.terminated = true
    this.initialized = false
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  GetValue(key: string): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.GET_BEFORE_INIT
      return ''
    }
    if (this.terminated) {
      this.lastError = ErrorCode.GET_AFTER_TERMINATE
      return ''
    }

    this.lastError = ErrorCode.NO_ERROR

    if (key === 'cmi._version') return '1.0'
    if (key === 'cmi.learner_name') return ''
    if (key === 'cmi.learner_id') return ''

    if (key.endsWith('._count')) {
      const prefix = key.replace('._count', '')
      let count = 0
      for (const k of Object.keys(this.data)) {
        const match = k.match(new RegExp(`^${prefix.replace('.', '\\.')}\\.(\\d+)\\.`))
        if (match) {
          const idx = Number.parseInt(match[1], 10) + 1
          if (idx > count) count = idx
        }
      }
      return String(count)
    }

    return this.data[key] ?? ''
  }

  SetValue(key: string, value: string): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.SET_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.SET_AFTER_TERMINATE
      return 'false'
    }

    const readOnlyKeys = ['cmi._version', 'cmi.learner_id', 'cmi.learner_name', 'cmi.entry', 'cmi.mode', 'cmi.credit']
    if (readOnlyKeys.includes(key)) {
      this.lastError = ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY
      return 'false'
    }

    this.data[key] = value
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  Commit(_param: string = ''): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.COMMIT_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.COMMIT_AFTER_TERMINATE
      return 'false'
    }

    const completionStatusMap: Record<string, string> = {
      completed: 'COMPLETED',
      incomplete: 'INCOMPLETE',
      'not attempted': 'NOT_ATTEMPTED',
      unknown: 'UNKNOWN',
    }

    const successStatusMap: Record<string, string> = {
      passed: 'PASSED',
      failed: 'FAILED',
      unknown: 'UNKNOWN',
    }

    // Accumulate total_time = previous total + current session_time (per SCORM 2004 spec)
    let accumulatedTotalTime: string | null = this.data['cmi.total_time'] || null
    const sessionTimeStr = this.data['cmi.session_time'] || null
    if (sessionTimeStr) {
      const sessionMs = iso8601DurationToMs(sessionTimeStr)
      accumulatedTotalTime = msToIso8601Duration(this.initialTotalTimeMs + sessionMs)
    }

    const scormData: ScormData = {
      completionStatus: completionStatusMap[this.data['cmi.completion_status'] || 'not attempted'] || 'NOT_ATTEMPTED',
      successStatus: successStatusMap[this.data['cmi.success_status'] || 'unknown'] || 'UNKNOWN',
      scoreRaw: sanitizeScore(this.data['cmi.score.raw']),
      scoreMin: sanitizeScore(this.data['cmi.score.min']),
      scoreMax: sanitizeScore(this.data['cmi.score.max']),
      // cmi.score.scaled est borné à [-1, 1] par la spec SCORM 2004.
      scoreScaled: sanitizeScore(this.data['cmi.score.scaled'], 1),
      totalTime: accumulatedTotalTime,
      sessionTime: sessionTimeStr,
      suspendData: this.data['cmi.suspend_data'] || null,
      location: this.data['cmi.location'] || null,
    }

    this.onCommit(scormData).catch(console.error)
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  GetLastError(): string {
    return String(this.lastError)
  }

  GetErrorString(errorCode: string): string {
    const code = Number.parseInt(errorCode, 10)
    const errorStrings: Record<number, string> = {
      [ErrorCode.NO_ERROR]: 'No Error',
      [ErrorCode.GENERAL_EXCEPTION]: 'General Exception',
      [ErrorCode.GENERAL_INITIALIZATION_FAILURE]: 'General Initialization Failure',
      [ErrorCode.ALREADY_INITIALIZED]: 'Already Initialized',
      [ErrorCode.CONTENT_INSTANCE_TERMINATED]: 'Content Instance Terminated',
      [ErrorCode.GENERAL_TERMINATION_FAILURE]: 'General Termination Failure',
      [ErrorCode.TERMINATE_BEFORE_INIT]: 'Termination Before Initialization',
      [ErrorCode.TERMINATE_AFTER_TERMINATE]: 'Termination After Termination',
      [ErrorCode.GET_BEFORE_INIT]: 'Retrieve Data Before Initialization',
      [ErrorCode.GET_AFTER_TERMINATE]: 'Retrieve Data After Termination',
      [ErrorCode.SET_BEFORE_INIT]: 'Store Data Before Initialization',
      [ErrorCode.SET_AFTER_TERMINATE]: 'Store Data After Termination',
      [ErrorCode.COMMIT_BEFORE_INIT]: 'Commit Before Initialization',
      [ErrorCode.COMMIT_AFTER_TERMINATE]: 'Commit After Termination',
      [ErrorCode.UNDEFINED_DATA_MODEL_ELEMENT]: 'Undefined Data Model Element',
      [ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY]: 'Data Model Element Is Read Only',
      [ErrorCode.DATA_MODEL_ELEMENT_IS_WRITE_ONLY]: 'Data Model Element Is Write Only',
    }
    return errorStrings[code] || 'Unknown Error'
  }

  GetDiagnostic(errorCode: string): string {
    return this.GetErrorString(errorCode)
  }
}

// SCORM 1.2 adapter that maps LMS* calls to the 2004 bridge
export function createScorm12Adapter(bridge: ScormApiBridge) {
  return {
    LMSInitialize: (param: string) => bridge.Initialize(param),
    LMSFinish: (param: string) => bridge.Terminate(param),
    LMSGetValue: (key: string) => bridge.GetValue(key),
    LMSSetValue: (key: string, value: string) => bridge.SetValue(key, value),
    LMSCommit: (param: string) => bridge.Commit(param),
    // eslint-disable-next-line unicorn/throw-new-error -- not an Error constructor, returns error code string
    LMSGetLastError: () => bridge.GetLastError(),
    LMSGetErrorString: (code: string) => bridge.GetErrorString(code),
    LMSGetDiagnostic: (code: string) => bridge.GetDiagnostic(code),
  }
}
